import {Box, Button, Input, Modal, Typography} from '@viptech/react-components'
import {useEffect, useState} from 'react'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import useClients from 'src/hooks/api/client/useClients'
import useCompanies from 'src/hooks/api/companies/useCompanies'
import useCreateLayout from 'src/hooks/api/layouts/useCreateLayout'
import useUpdateLayout from 'src/hooks/api/layouts/useUpdateLayout'
import {Layout} from 'src/services/api'

type LayoutRegisterModalProps = {
  isCreate: boolean
  openModal: boolean
  closeModal: () => void
  layout: Layout
  setLayout: (layout: Layout) => void
  loadLayouts: Function
  camerasIdsSelected: Array<number>
}

function LayoutRegisterModal(props: LayoutRegisterModalProps) {
  const {isCreate, openModal, closeModal, layout, setLayout, loadLayouts, camerasIdsSelected} = props

  const layoutTypesToList = [
    {id: '1', label: 'Padrão'},
    {id: '2', label: 'LPR'},
    {id: '3', label: 'Facial'},
  ]
  const [typeSelected, setTypeSelected] = useState<DropdownItem>()

  const {data: companies, isLoading: loadingCompany} = useCompanies()
  const [companySelected, setCompanySelected] = useState<DropdownItem>()

  const hasReadClientsPermission = ValidateUserPermissions('clients:read')
  const {data: clients} = useClients({selectedCompanies: [Number(companySelected?.id) ?? 0], hasPermission: hasReadClientsPermission && !!companySelected?.id})
  const [clientSelected, setClientSelected] = useState<DropdownItem>()

  useEffect(() => {
    if (!isCreate) {
      if (layout.layoutTypeId) setTypeSelected(layoutTypesToList.find((it) => Number(it.id) === layout.layoutTypeId))

      if (layout.clientId) {
        const client = clients?.data.data.entities.find((client) => client.id === layout.clientId)
        if (client) setClientSelected({id: String(client.id), label: client.name})
      }

      const company = companies?.pages.flatMap((it) => it.entities.find((company) => company.id === layout.companyId))
      if (company && company[0]) setCompanySelected({id: String(company[0].id), label: company[0].name})
    }
  }, [companies, clients])

  const {mutateAsync: updateLayout, isPending: loadingUpdateLayout} = useUpdateLayout()
  const {mutateAsync: createLayout, isPending: loadingCreateLayout} = useCreateLayout()

  const handleSaveButton = async () => {
    if (isCreate)
      await createLayout({
        layoutTypeId: layout.layoutTypeId,
        companyId: layout.companyId,
        name: layout.name,
        maxCameras: camerasIdsSelected.length,
        clientId: layout.clientId,
        cameraIds: camerasIdsSelected ?? [],
      })
    else
      await updateLayout({
        id: layout.id,
        body: {
          name: layout.name,
          maxCameras: camerasIdsSelected.length,
          clientId: layout.clientId,
          isClientDefault: layout.isClientDefault,
        },
        camerasIds: camerasIdsSelected ?? [],
      })
    closeModal()
    loadLayouts()
  }

  return (
    <Modal isOpen={openModal} closeModal={closeModal} backgroundColor='white' width='700px'>
      <Box padding='30px' display='flex' direction='column' rowGap='30px'>
        <Box display='flex' align='center'>
          <Typography variant='p' size='20px' color='#344054' children={isCreate ? 'Adicionar novo layout' : 'Editar Layout'}></Typography>
          <BtnClose onClose={closeModal} />
        </Box>

        <Box display='flex' direction='column' rowGap='15px'>
          <Input.Root
            labelFontSize='14px'
            borderColor='#D0D5DD'
            fontSize='12px'
            controlled
            label='Nome'
            required
            fontWeight='400'
            labelColor='#55565b'
            value={layout.name}
            onChange={(e) => setLayout({...layout, name: e.target.value})}
          />

          <Dropdown
            label='Tipo do Layout'
            items={layoutTypesToList}
            selected={typeSelected}
            fontSize='12px'
            required
            borderColor='#D0D5DD'
            onChangeSelected={(e) => {
              setTypeSelected(e)
              setLayout({...layout, layoutTypeId: Number(e.id)})
            }}
            disabled={!isCreate}
          />

          <Dropdown
            label='Empresa'
            fontSize='12px'
            borderColor='#D0D5DD'
            isLoading={loadingCompany}
            required
            items={
              companies?.pages.flatMap((it) =>
                it.entities.map((company) => {
                  return {
                    id: String(company.id),
                    label: company.name,
                  }
                }),
              ) ?? []
            }
            selected={companySelected}
            disabled={!isCreate}
            onChangeSelected={(e) => {
              setCompanySelected(e)
              setLayout({...layout, companyId: Number(e.id)})
            }}
          />

          {hasReadClientsPermission && (
            <Dropdown
              label='Cliente'
              fontSize='12px'
              borderColor='#D0D5DD'
              items={
                clients?.data.data.entities
                  .filter((it) => it.companyId === Number(companySelected?.id))
                  .map((client) => {
                    return {
                      id: String(client.id),
                      label: client.name,
                    }
                  }) ?? []
              }
              onChangeSelected={(e) => {
                setClientSelected(e)
                setLayout({...layout, clientId: Number(e.id)})
              }}
              selected={clientSelected}
            />
          )}
        </Box>

        <Box display='flex' width='100%' columnGap='10px' justifyContent='flex-end'>
          <Button variant='outlined' fontSize='12px' height='38px' width='120px' color='#8E8E8E' onClick={closeModal}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            fontSize='12px'
            height='38px'
            width='120px'
            disabledTextColor='#8E8E8E'
            onClick={() => handleSaveButton()}
            isLoading={loadingUpdateLayout || loadingCreateLayout}
            disabled={isCreate && (layout.name === '' || !typeSelected || !camerasIdsSelected.length || !companySelected)}>
            {isCreate ? 'Adicionar' : 'Salvar'}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default LayoutRegisterModal
